/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CreditCard, Info } from 'lucide-react';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';

function AutoRenewalSettings({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getSubscriptionSettings(workspaceId) });
      if (response.data) setSettings(response.data);
    } catch (err) {
      setError('Failed to load auto-renewal settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getActiveSubscription(workspaceId) });
      if (response.data) setSubscriptionInfo(response.data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    fetchSettings();
    fetchSubscriptionInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const handleToggleAutoRenewal = async (event) => {
    const newValue = event.target.checked;
    setUpdating(true);
    setError(null);
    setSuccessMessage('');
    try {
      const response = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.updateAutoRenewal(workspaceId),
        data: { autoRenewalEnabled: newValue },
      });
      if (response.success) {
        setSettings((prev) => ({ ...prev, autoRenewalEnabled: newValue }));
        setSuccessMessage(newValue
          ? 'Auto-renewal enabled. Your subscription will renew automatically.'
          : 'Auto-renewal disabled. You will need to manually renew your subscription.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setError('Failed to update auto-renewal setting. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const formatCardBrand = (brand) => (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card');
  const formatExpiry = (m, y) => (m && y ? `${String(m).padStart(2, '0')}/${y}` : '');
  const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not available');

  if (loading) return <p className="text-sm text-ink-500 py-6 text-center">Loading…</p>;
  if (!settings) return <p className="text-sm text-[#a8391d]">Unable to load auto-renewal settings. Please refresh the page.</p>;

  const pm = settings.paymentMethod;

  return (
    <div>
      <h5 className="text-base font-semibold text-ink-900 mb-3">Auto-Renewal Settings</h5>

      {error && <div className="text-sm rounded-[8px] px-3 py-2 mb-3" style={{ background: '#fdebe6', color: '#a8391d' }}>{error}</div>}
      {successMessage && <div className="text-sm rounded-[8px] px-3 py-2 mb-3" style={{ background: '#eef5ea', color: '#458a44' }}>{successMessage}</div>}

      <div className="rounded-[12px] border border-rule p-4" style={{ borderStyle: 'solid' }}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="text-sm font-semibold text-ink-900">Automatic Renewal</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-700">{settings.autoRenewalEnabled ? 'Enabled' : 'Disabled'}</span>
            <Switch
              checked={!!settings.autoRenewalEnabled}
              onChange={handleToggleAutoRenewal}
              disabled={updating || !settings.hasPaymentMethod}
              color="success"
            />
          </div>
        </div>
        <p className="text-sm text-ink-500">
          {settings.autoRenewalEnabled
            ? 'Your subscription will automatically renew on the billing date using your saved payment method.'
            : 'Auto-renewal is disabled. You will need to manually renew your subscription before it expires.'}
        </p>

        <div style={{ height: 1, background: '#ececf0', margin: '16px 0' }} />

        <span className="block text-sm font-semibold text-ink-900 mb-1.5">Payment Method</span>
        {settings.hasPaymentMethod && pm ? (
          <div className="flex items-center gap-2 text-sm text-ink-900">
            <CreditCard size={18} className="text-ink-500" />
            <span>
              {`${formatCardBrand(pm.brand)} ending in ${pm.last4}`}
              {pm.expMonth && pm.expYear ? <span className="text-ink-500">{`  ·  Expires ${formatExpiry(pm.expMonth, pm.expYear)}`}</span> : null}
            </span>
          </div>
        ) : (
          <div className="text-sm rounded-[8px] px-3 py-2" style={{ background: '#fde6d2', color: '#b9791f' }}>
            No payment method saved. Complete a subscription payment to enable auto-renewal.
          </div>
        )}

        {subscriptionInfo?.expiryDate && (
          <>
            <div style={{ height: 1, background: '#ececf0', margin: '16px 0' }} />
            <span className="block text-sm font-semibold text-ink-900 mb-1.5">Next Billing Date</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-ink-900">{formatDate(subscriptionInfo.expiryDate)}</span>
              {settings.autoRenewalEnabled && settings.hasPaymentMethod && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: '#eef5ea', color: '#458a44' }}>Will auto-renew</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Info box */}
      <div className="flex gap-2 rounded-[8px] mt-4 p-3" style={{ background: '#e8eef7' }}>
        <Info size={18} style={{ color: '#4062E5', flexShrink: 0, marginTop: 2 }} />
        <div>
          <p className="text-sm font-semibold text-ink-900 mb-1">How Auto-Renewal Works</p>
          <ul className="text-xs text-ink-700" style={{ lineHeight: 1.6, listStyle: 'disc', paddingLeft: 16 }}>
            <li>Your subscription will automatically renew on the billing date</li>
            <li>The saved payment method will be charged for the current number of active users</li>
            <li>You can disable auto-renewal at any time before the next billing date</li>
            <li>If payment fails, you&apos;ll have until the expiry date to update your payment method</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

AutoRenewalSettings.propTypes = { workspaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired };

export default AutoRenewalSettings;
